import * as React from 'react';
import { Box, Button, Chip, LinearProgress, MenuItem, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DropdownMenu } from 'components/DropdownMenu';
import { localizeDate, localizeNumber } from '@pec/aion-ui-i18next/helpers/localize';
import { QuestionStatusIcon } from '@pec/aion-ui-components/components/QuestionStatusIcon';
import { SegmentStatusIcon } from 'components/SegmentStatusIcon';
import { SvgIconComponent } from '@material-ui/icons';
import { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { useQuestionnaireProgress } from 'hooks/useQuestionnaireProgress';
import { useTranslation } from 'react-i18next';
import { Widget } from 'components/Widget';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      margin: theme.spacing(2, 1, 0)
    },
    progressValue: {
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: '24px',
      textAlign: 'center'
    },
    progressBar: {
      height: 4,
      marginBottom: theme.spacing(2)
    },
    progressBarBackground: {
      backgroundColor: theme.palette.grey[300]
    },
    buffer: {
      backgroundColor: '#f7d490'
    },
    dashed: {
      display: 'none'
    },
    unPublishedAnswersChip: {
      backgroundColor: 'var(--status-caution-23-)'
    },
    chipBox: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(1)
    },
    datesText: {
      color: 'var(--text-gray)',
      marginTop: theme.spacing(1)
    },
    menuTitle: {
      display: 'flex',
      gap: `${theme.spacing(2)}px`,
      padding: `${theme.spacing(1.5)}px ${theme.spacing(0.5)}px`
    },
    menuTitleIcon: {
      color: 'var(--light-outline)'
    },
    viewAllBtn: {
      marginTop: theme.spacing(2)
    }
  })
);

const getStatusCategories = (
  t: TFunction
): {
  Icon: SvgIconComponent | React.FC;
  label: string;
  dataKey: 'notAnswered' | 'inProgress' | 'answered';
}[] => [
  {
    label: t('dashboard.questionnaire.inProgress', 'In progress'),
    Icon: () => <QuestionStatusIcon status="inProgress" />,
    dataKey: 'inProgress'
  },
  {
    label: t('dashboard.questionnaire.notAnswered', 'Not answered'),
    Icon: () => <QuestionStatusIcon status="notStarted" />,
    dataKey: 'notAnswered'
  }
];

export const Questionnaire: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('subscriber');
  const { organizationId } = useParams<{
    organizationId: string;
  }>();

  const { hasContractorQuestionnairePermission } = usePermission();

  const {
    isFetching,
    error,
    hideProgressBar,
    progress,
    answersToBePublishedCount,
    mainSubscription,
    statusSegmentSections,
    lastPublishedDate,
    lastAnsweredDateTimeUtc,
    publishedProgress,
    draftAnswersProgress
  } = useQuestionnaireProgress();

  const pluralKey =
    answersToBePublishedCount === 1
      ? 'dashboard.questionnaire.unPublishedAnswersChip_one'
      : 'dashboard.questionnaire.unPublishedAnswersChip_other';

  let unpublishedAnswersLabel =
    ((t(pluralKey, {
      count: answersToBePublishedCount
    }) as unknown) as string) || '';

  if (!unpublishedAnswersLabel || unpublishedAnswersLabel.trim().length === 0) {
    unpublishedAnswersLabel =
      answersToBePublishedCount === 1
        ? `${answersToBePublishedCount} unpublished answer/upload`
        : `${answersToBePublishedCount} unpublished answers/uploads`;
  }

  if (!hasContractorQuestionnairePermission) {
    return null;
  }

  return (
    <Widget
      title={t('dashboard.questionnaire.title', 'Questionnaire')}
      isFetching={isFetching}
      hasFetchingError={!!error}
    >
      <Box className={classes.container}>
        {!hideProgressBar && (
          <>
            <LinearProgress
              variant="buffer"
              value={publishedProgress}
              valueBuffer={draftAnswersProgress + publishedProgress}
              classes={{
                root: classes.progressBar,
                colorPrimary: classes.progressBarBackground,
                dashed: classes.dashed,
                bar2Buffer: classes.buffer
              }}
            />
            <Typography classes={{ root: classes.progressValue }} variant="h6">
              {localizeNumber(
                {
                  value: publishedProgress / 100,
                  config: {
                    style: 'percent'
                  }
                },
                t
              )}{' '}
              {t('dashboard.questionnaire.completed', 'Completed')}
            </Typography>
            {answersToBePublishedCount > 0 && (
              <Box className={classes.chipBox}>
                <Chip
                  size="small"
                  label={unpublishedAnswersLabel}
                  className={classes.unPublishedAnswersChip}
                  data-testid="unpublished"
                />
              </Box>
            )}
            <Typography align="center" variant="body2" className={classes.datesText}>
              {t(
                'dashboard.questionnaire.dates',
                'Last answered: {{lastAnsweredDate}} | Last published: {{lastPublishedDate}}',
                {
                  lastAnsweredDate: localizeDate(lastAnsweredDateTimeUtc, t),
                  lastPublishedDate: localizeDate(lastPublishedDate, t)
                }
              )}
            </Typography>
          </>
        )}
        <Box px={0} pt={hideProgressBar ? 0 : 3}>
          {mainSubscription.countryISO2 === 'US' ? (
            <>
              {getStatusCategories(t).map(({ Icon, label, dataKey }) => {
                return progress[dataKey].length > 0 ? (
                  <DropdownMenu
                    key={dataKey}
                    title={
                      <Box className={classes.menuTitle}>
                        <Icon className={classes.menuTitleIcon} />
                        <Typography variant="body1">
                          {label} ({progress[dataKey].length})
                        </Typography>
                      </Box>
                    }
                  >
                    {progress[dataKey].map(sectionStatus => (
                      <MenuItem
                        key={sectionStatus.sectionId}
                        component="a"
                        href={`/organizations/${organizationId}/questionnaire-v2/${sectionStatus.sectionId}`}
                      >
                        {sectionStatus.sectionTitle}
                      </MenuItem>
                    ))}
                  </DropdownMenu>
                ) : null;
              })}
            </>
          ) : null}
          {mainSubscription.countryISO2 !== 'US'
            ? Object.keys(statusSegmentSections).map(statusSegmentSectionKey => {
                return Object.keys(statusSegmentSections[statusSegmentSectionKey]).length > 0 ? (
                  <DropdownMenu
                    key={statusSegmentSectionKey}
                    title={
                      <Box className={classes.menuTitle}>
                        <SegmentStatusIcon segmentStatus={statusSegmentSectionKey} />
                        <Typography variant="body1">
                          {statusSegmentSectionKey} (
                          {Object.keys(statusSegmentSections[statusSegmentSectionKey]).length})
                        </Typography>
                      </Box>
                    }
                  >
                    {Object.keys(statusSegmentSections[statusSegmentSectionKey]).map(moduleKey => {
                      const [module] = statusSegmentSections[statusSegmentSectionKey][moduleKey];
                      return (
                        <MenuItem
                          key={module.id}
                          component="a"
                          href={`/organizations/${organizationId}/questionnaire-v2/${module.id}`}
                        >
                          {module.moduleTitle}
                        </MenuItem>
                      );
                    })}
                  </DropdownMenu>
                ) : null;
              })
            : null}
        </Box>
        <Button
          variant="contained"
          color="primary"
          className={classes.viewAllBtn}
          component="a"
          href={`/organizations/${organizationId}/questionnaire-v2`}
        >
          {t('dashboard.questionnaire.goToQuestionnaire', 'go to questionnaire')}
        </Button>
      </Box>
    </Widget>
  );
};
